import clsx from 'clsx'
import { useDrillDown } from '@/components/dashboards/useDrillDown'
import {
  NOT_RECORDED_COLOR,
  compactCurrency,
  drillForIds,
  goldCategory,
  groupLabel,
  type PivotRow,
} from '@/services/dashboardAnalytics'

interface MixBarsProps {
  title: string
  rows: readonly PivotRow[] | undefined
  /** Drill title prefix — the person's name. */
  scopeLabel: string
  /** Rows drawn; the rest are summed into one "n more" line (still drillable). */
  limit?: number
  /** Keep the server's order (bands) instead of ranking by size. */
  ordered?: boolean
  className?: string
}

/**
 * One mix as a ranked bar list: share of the person's deals by count, with
 * the volume beside it. Every row — including "n more" — opens its deals.
 */
export default function MixBars({ title, rows = [], scopeLabel, limit = 6, ordered = false, className }: MixBarsProps) {
  const { open } = useDrillDown()
  const ranked = ordered ? [...rows] : [...rows].sort((a, b) => b.measures.count - a.measures.count)
  const shown = ranked.slice(0, limit)
  const rest = ranked.slice(limit)
  const total = rows.reduce((sum, row) => sum + row.measures.count, 0)
  const max = Math.max(1, ...shown.map((row) => row.measures.count))
  const restIds = rest.flatMap((row) => row.measures.dealIds)
  const restCount = rest.reduce((sum, row) => sum + row.measures.count, 0)

  return (
    <div className={clsx('min-w-0', className)}>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="font-mono text-[8.5px] uppercase tracking-[0.18em] text-stone-500">{title}</span>
        <span className="font-mono text-[9px] text-stone-600">{rows.length} groups</span>
      </div>
      {shown.length === 0 ? (
        <p className="text-[10.5px] text-stone-600">Nothing recorded.</p>
      ) : (
        <ul className="space-y-1">
          {shown.map((row, index) => {
            const label = groupLabel(row.key, row.label)
            const share = total ? (row.measures.count / total) * 100 : 0
            return (
              <li key={row.key ?? '∅'}>
                <button
                  type="button"
                  onClick={() => open(drillForIds(row.measures.dealIds, `${scopeLabel} — ${label}`))}
                  className="group grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-2 rounded px-0.5 text-left hover:bg-amber-400/[0.04] focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/50"
                  title={`${label}: ${row.measures.count} deals (${share.toFixed(0)}%) · ${compactCurrency(row.measures.commitment)}`}
                >
                  <span className="min-w-0">
                    <span className={clsx('block truncate text-[10.5px]', row.key == null ? 'italic text-stone-500' : 'text-stone-300 group-hover:text-amber-100')}>
                      {label}
                    </span>
                    <span className="mt-0.5 block h-[3px] rounded-full bg-white/[0.04]">
                      <span
                        className="block h-full rounded-full"
                        style={{
                          width: `${Math.max(3, (row.measures.count / max) * 100)}%`,
                          background: row.key == null ? NOT_RECORDED_COLOR : goldCategory(index),
                        }}
                      />
                    </span>
                  </span>
                  <span className="font-mono text-[10.5px] tabular-nums text-stone-200">{row.measures.count}</span>
                  <span className="w-[52px] text-right font-mono text-[9.5px] tabular-nums text-stone-500">
                    {compactCurrency(row.measures.commitment)}
                  </span>
                </button>
              </li>
            )
          })}
          {restCount > 0 && (
            <li>
              <button
                type="button"
                onClick={() => open(drillForIds(restIds, `${scopeLabel} — ${title}: ${rest.length} more`))}
                className="w-full rounded px-0.5 text-left text-[10px] text-stone-500 hover:text-amber-200"
              >
                + {rest.length} more · {restCount} deals
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
